import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BookOpen, ShoppingBag } from "lucide-react";

const isBody = (t) => !/^(## |> |— )/.test(t) && t.length > 80;

function SampleBlock({ text, dropCap }) {
  if (text.startsWith("## ")) return <h3 className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-sans font-semibold pt-4">{text.slice(3)}</h3>;
  if (text.startsWith("> ")) return <p className="italic text-[#A8A29E] border-l-2 border-[#D4AF37]/50 pl-5">{text.slice(2)}</p>;
  if (text.startsWith("— ")) return <p className="text-right text-[#D4AF37]">{text}</p>;
  return (
    <p className={dropCap ? "first-letter:text-5xl first-letter:font-semibold first-letter:text-[#D4AF37] first-letter:float-left first-letter:mr-2 first-letter:leading-none" : ""}>{text}</p>
  );
}

export function SampleReader({ book, open, onOpenChange }) {
  if (!book) return null;
  const hasSample = Array.isArray(book.sample) && book.sample.length > 0;
  const firstBodyIdx = hasSample ? book.sample.findIndex(isBody) : -1;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col bg-[#0A0A0A] border-[#D4AF37]/40 rounded-none p-0 text-[#F5F3EF]" data-testid="sample-reader">
        <DialogHeader className="px-8 pt-8 pb-5 border-b border-[#D4AF37]/20 text-left">
          <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-2">Read a Sample</div>
          <DialogTitle className="font-serif-display text-3xl font-medium leading-tight">{book.title}</DialogTitle>
          <DialogDescription className="text-[#A8A29E]">{book.subtitle} · by {book.author}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-8 py-8 doc-scroll" data-testid="sample-reader-body">
          {hasSample ? (
            <div className="font-serif-display text-lg leading-[1.85] text-[#EDE9E1] space-y-6 max-w-2xl">
              {book.sample.map((p, i) => <SampleBlock key={i} text={p} dropCap={i === firstBodyIdx} />)}
              <div className="gold-rule mt-10" />
              <p className="text-sm text-[#A8A29E] not-italic font-sans">End of sample. Continue reading in the full edition.</p>
            </div>
          ) : (
            <div className="text-center py-12" data-testid="sample-reader-empty">
              <BookOpen className="w-10 h-10 text-[#D4AF37]/60 mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-serif-display text-2xl">Sample Coming Soon</p>
              <p className="text-sm text-[#A8A29E] mt-2 max-w-md mx-auto">A preview of the opening pages of this title will be available here shortly.</p>
            </div>
          )}
        </div>

        <div className="px-8 py-5 border-t border-[#D4AF37]/20 flex flex-wrap items-center justify-between gap-4 bg-[#141414]">
          <div className="text-sm text-[#A8A29E]">{book.format} · {book.price}</div>
          <a href={book.url} target="_blank" rel="noopener noreferrer" data-testid="sample-reader-buy-btn">
            <Button className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white px-7">
              <ShoppingBag className="mr-2 w-4 h-4" /> Purchase the Full Book
            </Button>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
